import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DatePicker, Select, Switch, Input, InputNumber, Button, Card, Row, Col, message, Modal, Table, Space } from 'antd';
import { FileText, Eye, Plus, Trash2, Printer, Image as ImageIcon, FileCheck } from 'lucide-react';
import QuotePreview from '../components/QuotePreview';
import { numberToWords as toWords, formatIndianCurrency } from '../utils/numberToWords';
import { useReactToPrint } from 'react-to-print';
import { addQuotation, updateQuoteNumber, fetchClients, addClient as apiAddClient, fetchUsers } from '../utils/api';
import { numberToWords } from '../utils/numberToWords';

const { TextArea } = Input;

function CreateQuote({ clients = [], config = {}, user = {}, onQuoteNumberUpdate = () => { } }) {
  const [clientList, setClientList] = useState(clients || []);
  const [selectedClient, setSelectedClient] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [shipToAddress, setShipToAddress] = useState('');
  const [clientContact, setClientContact] = useState('');
  const [manualClientMode, setManualClientMode] = useState(false);
  const [manualClient, setManualClient] = useState({ name: '', billToAddress: '', shipToAddress: '', contact: '' });
  const [reference, setReference] = useState(user?.name || '');
  const [users, setUsers] = useState([]);
  const [placeOfSupply, setPlaceOfSupply] = useState('29');

  const placeOfSupplyOptions = [
    { label: 'Jammu & Kashmir (01)', value: '01' },
    { label: 'Himachal Pradesh (02)', value: '02' },
    { label: 'Punjab (03)', value: '03' },
    { label: 'Chandigarh (04)', value: '04' },
    { label: 'Uttarakhand (05)', value: '05' },
    { label: 'Haryana (06)', value: '06' },
    { label: 'Delhi (07)', value: '07' },
    { label: 'Rajasthan (08)', value: '08' },
    { label: 'Uttar Pradesh (09)', value: '09' },
    { label: 'Bihar (10)', value: '10' },
    { label: 'Sikkim (11)', value: '11' },
    { label: 'Arunachal Pradesh (12)', value: '12' },
    { label: 'Nagaland (13)', value: '13' },
    { label: 'Manipur (14)', value: '14' },
    { label: 'Mizoram (15)', value: '15' },
    { label: 'Tripura (16)', value: '16' },
    { label: 'Meghalaya (17)', value: '17' },
    { label: 'Assam (18)', value: '18' },
    { label: 'West Bengal (19)', value: '19' },
    { label: 'Jharkhand (20)', value: '20' },
    { label: 'Odisha (21)', value: '21' },
    { label: 'Chhattisgarh (22)', value: '22' },
    { label: 'Madhya Pradesh (23)', value: '23' },
    { label: 'Gujarat (24)', value: '24' },
    { label: 'Dadra and Nagar Haveli and Daman and Diu (26)', value: '26' },
    { label: 'Maharashtra (27)', value: '27' },
    { label: 'Karnataka (29)', value: '29' },
    { label: 'Goa (30)', value: '30' },
    { label: 'Lakshadweep (31)', value: '31' },
    { label: 'Kerala (32)', value: '32' },
    { label: 'Tamil Nadu (33)', value: '33' },
    { label: 'Puducherry (34)', value: '34' },
    { label: 'Andaman & Nicobar Islands (35)', value: '35' },
    { label: 'Telangana (36)', value: '36' },
    { label: 'Andhra Pradesh (New) (37)', value: '37' },
    { label: 'Ladakh (38)', value: '38' },
    { label: 'Other Territory (97)', value: '97' },
    { label: 'Center Jurisdiction (99)', value: '99' }
  ];

  const [quoteNumber, setQuoteNumber] = useState(() => {
    const month = new Date().toLocaleString('en-IN', { month: 'short' }).toUpperCase();
    const n = (config?.nextQuoteNumber || 1).toString().padStart(3, '0');
    const prefix = config?.quotePrefix || 'QUO';
    return `${prefix}-${month}-${n}`;
  });

  useEffect(() => {
    const month = new Date().toLocaleString('en-IN', { month: 'short' }).toUpperCase();
    const n = (config?.nextQuoteNumber || 1).toString().padStart(3, '0');
    const prefix = config?.quotePrefix || 'QUO';
    setQuoteNumber(`${prefix}-${month}-${n}`);
  }, [config?.quotePrefix, config?.nextQuoteNumber]);

  const [quoteDate, setQuoteDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [financialYear, setFinancialYear] = useState('');
  const [roundOffEnabled, setRoundOffEnabled] = useState(true);
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' | 'amount'
  const [discountRate, setDiscountRate] = useState(0);
  const [saving, setSaving] = useState(false);
  const [processingImages, setProcessingImages] = useState({});
  const [items, setItems] = useState([
    { description: '', hsn: '', qty: 1, rate: 0, baseAmount: 0, taxPercent: 0, taxAmount: 0, totalAmount: 0, imageBase64: '', imageName: '' }
  ]);

  useEffect(() => {
    try {
      const d = new Date(quoteDate);
      if (!isNaN(d)) {
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const startYear = month >= 4 ? year : year - 1;
        setFinancialYear(`${startYear}-${startYear + 1}`);
      }
    } catch { }
  }, [quoteDate]);

  useEffect(() => {
    if (!selectedClient || manualClientMode) return;
    const c = (clientList || []).find(x => x._id === selectedClient);
    if (c) {
      setBillToAddress(c.billToAddress || c.address || '');
      setShipToAddress(c.shipToAddress || c.billToAddress || c.address || '');
      setClientContact(c.phone || c.email || '');
    }
  }, [selectedClient, clientList, manualClientMode]);

  useEffect(() => {
    if (manualClientMode) {
      setBillToAddress(manualClient.billToAddress);
      setShipToAddress(manualClient.shipToAddress);
      setClientContact(manualClient.contact);
    } else {
      setBillToAddress('');
      setShipToAddress('');
      setClientContact('');
      setSelectedClient('');
    }
  }, [manualClientMode]);

  useEffect(() => {
    const load = async () => {
      try { setClientList(await fetchClients()); } catch { }
    };
    if (!clients || clients.length === 0) load();
  }, [clients]);

  useEffect(() => {
    const loadUsers = async () => {
      try { setUsers(await fetchUsers() || []); } catch { }
    };
    loadUsers();
  }, []);

  const handleItemChange = (idx, field, value) => {
    const next = [...items];
    next[idx][field] = value;
    const qty = Number(next[idx].qty || 0);
    const rate = Number(next[idx].rate || 0);
    const base = field !== 'rate' && field !== 'qty' ? Number(next[idx].baseAmount) : qty * rate;
    const pct = Number(next[idx].taxPercent || 0);
    const tax = field === 'taxAmount' ? Number(next[idx].taxAmount || 0) : base * pct / 100;
    const total = field === 'totalAmount' ? Number(next[idx].totalAmount || 0) : base + tax;
    next[idx].baseAmount = base;
    next[idx].taxAmount = tax;
    next[idx].totalAmount = total;
    setItems(next);
  };

  const addRow = () => setItems(prev => ([...prev, { description: '', hsn: '', qty: 1, rate: 0, baseAmount: 0, taxPercent: 0, taxAmount: 0, totalAmount: 0, imageBase64: '', imageName: '' }]));
  const removeRow = (idx) => setItems(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
        canvas.width = width; canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const onImageChange = async (idx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { message.error('Image too large (max 5MB)'); return; }
    setProcessingImages(prev => ({ ...prev, [idx]: true }));
    try {
      const compressedBase64 = await compressImage(file);
      const next = [...items];
      next[idx].imageBase64 = compressedBase64;
      next[idx].imageName = file.name;
      setItems(next);
    } catch { message.error('Error processing image'); }
    finally { setProcessingImages(prev => ({ ...prev, [idx]: false })); }
  };

  const removeImage = (idx) => {
    const next = [...items];
    next[idx].imageBase64 = '';
    next[idx].imageName = '';
    setItems(next);
  };

  const totals = useMemo(() => {
    const subtotal = Math.round(items.reduce((s, it) => s + Number(it.baseAmount || 0), 0) * 100) / 100;
    const safeDiscountRate = Math.max(0, Number(discountRate) || 0);
    let discount = 0;
    if (discountType === 'percentage') {
      const pct = Math.min(100, safeDiscountRate);
      discount = Math.round(((subtotal * pct) / 100) * 100) / 100;
    } else {
      discount = Math.min(subtotal, Math.round(safeDiscountRate * 100) / 100);
    }

    const discountRatio = subtotal > 0 ? (discount / subtotal) : 0;
    const tax = Math.round(items.reduce((s, it) => {
      const itemBase = Number(it.baseAmount || 0);
      const discountedItemBase = itemBase * (1 - discountRatio);
      const itemTaxPercent = Number(it.taxPercent || 0);
      return s + (discountedItemBase * itemTaxPercent / 100);
    }, 0) * 100) / 100;

    const gross = Math.round(((subtotal - discount) + tax) * 100) / 100;
    const roundedTotal = Math.round(gross);
    const roundOff = roundOffEnabled ? Math.round((roundedTotal - gross) * 100) / 100 : 0;
    const total = roundOffEnabled ? roundedTotal : gross;
    return {
      subtotal,
      discount,
      discountRate: safeDiscountRate,
      discountType,
      tax,
      roundOff,
      total
    };
  }, [items, discountType, discountRate, roundOffEnabled]);

  const [showPreview, setShowPreview] = useState(false);
  const printRef = React.useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const handleSave = async () => {
    if (!manualClientMode && !selectedClient) { message.error('Please select a client'); return; }
    if (manualClientMode && !manualClient.name) { message.error('Please enter client name'); return; }
    if (items.some(it => !it.description)) { message.error('Please fill all item descriptions'); return; }

    const payload = {
      client: manualClientMode ? null : selectedClient,
      clientName: manualClientMode ? manualClient.name : null,
      billToAddress, shipToAddress, clientContact, quoteNumber, quoteDate, financialYear, reference, placeOfSupply, items,
      totals: {
        subtotal: totals.subtotal,
        discount: totals.discount,
        discountRate: totals.discountRate,
        discountType: totals.discountType,
        tax: totals.tax,
        roundOff: totals.roundOff,
        total: totals.total
      },
      amountInWords: numberToWords(totals.total)
    };
    setSaving(true);
    try {
      await addQuotation(payload);
      const nextNumber = Number(config?.nextQuoteNumber || 1) + 1;
      try { await updateQuoteNumber(nextNumber); onQuoteNumberUpdate(nextNumber); } catch { }
      message.success('Quotation created successfully!');
      // Reset form
      setSelectedClient(''); setBillToAddress(''); setShipToAddress(''); setClientContact('');
      setManualClient({ name: '', billToAddress: '', shipToAddress: '', contact: '' });
      setReference(user?.name || ''); setPlaceOfSupply('');
      setDiscountType('percentage'); setDiscountRate(0);
      const month = new Date().toLocaleString('en-IN', { month: 'short' }).toUpperCase();
      setQuoteNumber(`${config?.quotePrefix || 'QUO'}-${month}-${nextNumber.toString().padStart(3, '0')}`);
      setItems([{ description: '', hsn: '', qty: 1, rate: 0, baseAmount: 0, taxPercent: 0, taxAmount: 0, totalAmount: 0, imageBase64: '', imageName: '' }]);
    } catch (e) { message.error(e.message || 'Failed to create quotation'); }
    finally { setSaving(false); }
  };

  const selectedClientData = (clientList || []).find(c => c._id === selectedClient);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Create Quote</h1>
          <p>Build a quotation with item images and GST breakdown.</p>
        </div>
      </div>

      {/* General Information Card - 3 Column Layout like Create Invoice */}
      <Card
        title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileCheck size={18} style={{ color: '#2563eb' }} /> General Information</span>}
        extra={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 12, color: '#666' }}>External Client</span><Switch size="small" checked={manualClientMode} onChange={setManualClientMode} /></div>}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Quote Number</label>
              <Input value={quoteNumber} disabled style={{ background: '#f5f5f5' }} />
            </div>
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Quote Date</label>
              <DatePicker style={{ width: '100%' }} value={quoteDate ? dayjs(quoteDate) : null} onChange={d => setQuoteDate(d ? d.format('YYYY-MM-DD') : '')} format="DD/MM/YYYY" />
            </div>
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Reference</label>
              <Select style={{ width: '100%' }} value={reference || undefined} onChange={setReference}
                placeholder="Select reference" options={users.map(u => ({ label: u.name, value: u.name }))} showSearch optionFilterProp="label" allowClear />
            </div>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Client Selection</label>
              {!manualClientMode ? (
                <Select style={{ width: '100%' }} value={selectedClient || undefined} onChange={setSelectedClient}
                  placeholder="Select a client" options={(clientList || []).map(c => ({ label: c.name, value: c._id }))}
                  showSearch optionFilterProp="label" allowClear />
              ) : (
                <Input value={manualClient.name} onChange={e => setManualClient(p => ({ ...p, name: e.target.value }))} placeholder="Enter client name" />
              )}
            </div>
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Financial Year</label>
              <Input value={financialYear} disabled style={{ background: '#f5f5f5' }} />
            </div>
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Place of Supply</label>
              <Select style={{ width: '100%' }} value={placeOfSupply || undefined} onChange={setPlaceOfSupply}
                placeholder="Select place" options={placeOfSupplyOptions} showSearch optionFilterProp="label" allowClear />
            </div>
          </Col>
        </Row>

        {/* Client Details & Addresses */}
        {(selectedClientData || manualClientMode) && (
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}>
              {selectedClientData && !manualClientMode && (
                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Client Details</div>
                  <div style={{ fontWeight: 500 }}>{selectedClientData.registeredName || selectedClientData.name}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>GSTIN: {selectedClientData.gstin || '-'}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Phone: {selectedClientData.phone || '-'}</div>
                </div>
              )}
            </Col>
            <Col span={8}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Bill To Address</label>
                <TextArea rows={3} value={manualClientMode ? manualClient.billToAddress : billToAddress}
                  onChange={e => manualClientMode ? setManualClient(p => ({ ...p, billToAddress: e.target.value })) : setBillToAddress(e.target.value)} />
              </div>
            </Col>
            <Col span={8}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Ship To Address</label>
                <TextArea rows={3} value={manualClientMode ? manualClient.shipToAddress : shipToAddress}
                  onChange={e => manualClientMode ? setManualClient(p => ({ ...p, shipToAddress: e.target.value })) : setShipToAddress(e.target.value)} />
              </div>
            </Col>
          </Row>
        )}
      </Card>

      {/* Line Items Card */}
      <Card title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileText size={18} style={{ color: '#2563eb' }} /> Line Items</span>}>
        <Table
          dataSource={items.map((it, idx) => ({ ...it, key: idx, index: idx }))}
          pagination={false}
          size="small"
          bordered
          columns={[
            { title: '#', dataIndex: 'index', width: 40, render: (_, __, idx) => idx + 1 },
            {
              title: 'Item & Description', dataIndex: 'description', width: 280,
              render: (val, record) => (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Input placeholder="Description" value={val} onChange={e => handleItemChange(record.index, 'description', e.target.value)} style={{ flex: 1 }} />
                    <Button
                      icon={processingImages[record.index] ? <span>...</span> : <ImageIcon size={16} />}
                      onClick={() => document.getElementById(`img-${record.index}`).click()}
                    />
                    <input id={`img-${record.index}`} type="file" accept="image/*" onChange={e => onImageChange(record.index, e.target.files[0])} style={{ display: 'none' }} />
                  </div>
                  {record.imageBase64 && (
                    <div style={{ marginTop: 4, padding: 4, background: '#f8f9fa', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={record.imageBase64} alt="" style={{ maxHeight: 40, maxWidth: 60, objectFit: 'contain' }} />
                      <span style={{ fontSize: 10, flex: 1 }}>{record.imageName}</span>
                      <Button type="text" danger size="small" onClick={() => removeImage(record.index)}>×</Button>
                    </div>
                  )}
                </div>
              )
            },
            { title: 'HSN/SAC', dataIndex: 'hsn', width: 100, render: (val, record) => <Input value={val} onChange={e => handleItemChange(record.index, 'hsn', e.target.value)} placeholder="HSN" /> },
            { title: 'Qty', dataIndex: 'qty', width: 80, render: (val, record) => <InputNumber value={val} onChange={v => handleItemChange(record.index, 'qty', v)} style={{ width: '100%' }} min={0} /> },
            { title: 'Rate', dataIndex: 'rate', width: 100, render: (val, record) => <InputNumber value={val} onChange={v => handleItemChange(record.index, 'rate', v)} style={{ width: '100%' }} min={0} /> },
            { title: 'Base Amt', dataIndex: 'baseAmount', width: 110, render: (val, record) => <InputNumber value={val} onChange={v => handleItemChange(record.index, 'baseAmount', v)} style={{ width: '100%' }} min={0} /> },
            {
              title: 'GST %', dataIndex: 'taxPercent', width: 90,
              render: (val, record) => (
                <Select value={val} onChange={v => handleItemChange(record.index, 'taxPercent', v)} style={{ width: '100%' }}>
                  <Select.Option value={0}>0%</Select.Option>
                  <Select.Option value={5}>5%</Select.Option>
                  <Select.Option value={12}>12%</Select.Option>
                  <Select.Option value={18}>18%</Select.Option>
                  <Select.Option value={28}>28%</Select.Option>
                </Select>
              )
            },
            { title: 'Tax Amt', dataIndex: 'taxAmount', width: 100, render: (val, record) => <InputNumber value={val} onChange={v => handleItemChange(record.index, 'taxAmount', v)} style={{ width: '100%' }} min={0} /> },
            { title: 'Total', dataIndex: 'totalAmount', width: 110, render: (val, record) => <InputNumber value={val} onChange={v => handleItemChange(record.index, 'totalAmount', v)} style={{ width: '100%' }} min={0} /> },
            {
              title: 'Action', width: 60, align: 'center',
              render: (_, record) => items.length > 1 && <Button type="text" danger icon={<Trash2 size={14} />} onClick={() => removeRow(record.index)} />
            }
          ]}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 16 }}>
          <Button icon={<Plus size={14} />} onClick={addRow}>Add Item</Button>
          <div style={{ textAlign: 'right', minWidth: 300 }}>
            <div style={{ marginBottom: 8 }}><span>Subtotal: </span><strong>{formatIndianCurrency(totals.subtotal)}</strong></div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
              <span style={{ color: '#4b5563' }}>Discount:</span>
              <Space.Compact size="small">
                <Select
                  value={discountType}
                  onChange={setDiscountType}
                  style={{ width: 60 }}
                >
                  <Select.Option value="percentage">%</Select.Option>
                  <Select.Option value="amount">₹</Select.Option>
                </Select>
                <InputNumber
                  min={0}
                  max={discountType === 'percentage' ? 100 : totals.subtotal}
                  value={discountRate}
                  onChange={v => setDiscountRate(v ?? 0)}
                  placeholder="0"
                  style={{ width: 85 }}
                />
              </Space.Compact>
              {totals.discount > 0 && (
                <strong style={{ color: '#16a34a', minWidth: 70, textAlign: 'right' }}>
                  -{formatIndianCurrency(totals.discount)}
                </strong>
              )}
            </div>
            <div style={{ marginBottom: 8 }}><span>Tax: </span><strong>{formatIndianCurrency(totals.tax)}</strong></div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
              <Switch size="small" checked={roundOffEnabled} onChange={setRoundOffEnabled} />
              <span>Round Off: </span><strong>{formatIndianCurrency(totals.roundOff)}</strong>
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#2563eb' }}>Total: {formatIndianCurrency(totals.total)}</div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, marginBottom: 24 }}>
        <Button type="primary" icon={<FileText size={14} />} onClick={handleSave} loading={saving} >Create Quote</Button>
      </div>

      <Modal open={showPreview && (selectedClient || manualClientMode)} onCancel={() => setShowPreview(false)} width={900} footer={null}
        title={<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span>{quoteNumber}</span>
          <Button type="primary" icon={<Printer size={14} />} onClick={handlePrint}>Print</Button></div>}>
        <QuotePreview client={manualClientMode ? null : clientList.find(c => c._id === selectedClient)}
          clientName={manualClientMode ? manualClient.name : null} billToAddress={manualClientMode ? manualClient.billToAddress : billToAddress}
          shipToAddress={manualClientMode ? manualClient.shipToAddress : shipToAddress} clientContact={manualClientMode ? manualClient.contact : clientContact}
          quoteNumber={quoteNumber} quoteDate={quoteDate} reference={reference} placeOfSupply={placeOfSupply}
          items={items} totals={totals} amountInWords={toWords(totals.total)} printRef={printRef} handlePrint={handlePrint} config={config} />
      </Modal>
    </div>
  );
}

export default CreateQuote;
